import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactComponent as DownIcon } from '../assets/icons/main/chevron-down-icon.svg';

import CustomDataTable from '../components/CustomDataTable';
import SelectComponent from '../components/Select';
import DeleteIconButton from '../components/DeleteIconButton';
import TruncatedTypography from '../components/TruncatedTypography';
import SearchField from './searchField';
import { palette } from '../theme/palette';

type ValidationRow = {
  id: string;
  asset_index: number;
  component_index: number;
  asset_name: string;
  asset_ip: string;
  asset_status: string;
  component_name: string;
  component_status: string;
  secrets?: Record<string, string>;
  status: 'Error' | 'Valid';
  issues: string;
};

type SecretFieldDef = {
  key: string;
  label: string;
  isDropdown?: boolean;
  options?: string[];
};

type FlowImportValidationModalProps = {
  open: boolean;
  isLoading?: boolean;
  selectedGatewayName: string;
  parserWarnings: string[];
  validationErrors: string[];
  validationRows: ValidationRow[];
  componentOptions: { label: string; value: string }[];
  componentSecretFieldsMap: Record<string, SecretFieldDef[]>;
  componentRequiredSecretsMap: Record<string, string[]>;
  secretsData: any[];
  canSave: boolean;
  onClose: () => void;
  onSkipScan: (rows: ValidationRow[]) => void;
  onScanModified: (rows: ValidationRow[]) => void;
  onSave: (rows: ValidationRow[]) => void;
};

type SortState = {
  field: string;
  sort: '' | 'asc' | 'desc';
};

const STATUS_OPTIONS = [
  { value: 'Enable', label: 'Enable' },
  { value: 'Disable', label: 'Disable' },
];

const TABLE_ROW_HEIGHT = 78;

const Styles = {
  dialog: {
    muiPaper: {
      maxWidth: '800px',
      // padding: '21px 32px 32px',
      width: '800px',
      borderRadius: '16px',
    },
    // '& .MuiPaper-root:not(.MuiAccordion-root)': {
    // maxWidth: '800px',
    // padding: '21px 32px',
    // width: '800px',
    // borderRadius: '18px',
    //   backgroundColor: `${palette.transparent.main}`,
    // },
    // '&.MuiPaper-root.MuiDialog-root': {
    //   maxWidth: '800px',
    //   padding: '21px 32px',
    //   width: '800px',
    //   borderRadius: '18px',
    //   backgroundColor: `${palette.transparent.main}`,
    // },
  },
  dialogTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.3rem',
    padding: '0px',
    fontFamily: 'Open Sans',
    fontWeight: '400',
  },
  content: {
    '&.MuiDialogContent-root': {
      padding: '0px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '0px',
      gap: '0px',
      flex: 0
    },
  },
  closeIcon: {
    cursor: 'pointer',
  },
};

const cellSelectSx = {
  borderRadius: '8px',
  fontSize: '12px',
  backgroundColor: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9DFED',
  },
  '& .MuiSelect-select': {
    display: 'flex'
  },
};

const validationTooltipSx = {
  fontSize: '0.92rem',
  lineHeight: 1.35,
  px: 1.5,
  py: 1,
  maxWidth: 360,
};

const noWrapHeader = (label: string) => <Box component='span' sx={{ whiteSpace: 'nowrap' }}>{label}</Box>;

// const helperTextSx = (error = false) => ({
//   minHeight: '16px',
//   lineHeight: 1.2,
//   fontSize: '0.75rem',
//   fontWeight: 400,
//   color: error ? palette.error.main : palette.warning.main,
//   marginTop: '2px',
//   textAlign: 'left',
//   overflow: 'hidden',
//   textOverflow: 'ellipsis',
//   whiteSpace: 'nowrap',
//   display: 'block',
//   width: '100%',
// });

// const CellStack = ({
//   children,
//   helperText,
//   helperError = false,
// }: {
//   children: React.ReactNode;
//   helperText?: string;
//   helperError?: boolean;
// }) => (
//   <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25, py: 0.5 }}>
//     <Box sx={{ width: '100%' }}>{children}</Box>
//     <Typography variant='caption' sx={helperTextSx(helperError)} title={helperText || ''}>
//       {helperText || ' '}
//     </Typography>
//   </Box>
// );


const helperTextSx = (error = false) => ({
  minHeight: '16px',
  lineHeight: 1.2,
  fontSize: '0.75rem',
  fontWeight: 400,
  color: error ? palette.error.main : palette.warning.main,
  marginTop: '2px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
});

const CellStack = ({
  children,
  helperText,
  helperError = false,
}: {
  children: React.ReactNode;
  helperText?: string;
  helperError?: boolean;
}) => {
  const helper = String(helperText || '');

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        py: 0.5,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>{children}</Box>

      <Tooltip
        title={helper}
        placement='right'
        arrow
        disableHoverListener={!helper.trim()}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 200,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },         
        }}
      >
        <Typography variant='caption' sx={helperTextSx(helperError)}>
          {helper || ' '}
        </Typography>
      </Tooltip>
    </Box>
  );
};

type ValidationTextCellProps = {
  rowId: string;
  value: string;
  error?: boolean;
  warning?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  helperError?: boolean;
  onChange: (rowId: string, value: string) => void;
};

const ValidationTextCell = memo(({ rowId, value, error = false, warning = false, disabled = false, placeholder = '', helperText = '', helperError = false, onChange }: ValidationTextCellProps) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(rowId, e.target.value), [onChange, rowId]);
  const borderColor = error
    ? palette.error.main
    : warning
      ? palette.warning.main
      : palette.secondary.main;
  return (
    <CellStack helperText={helperText} helperError={helperError}>
      <TextField
        value={value}
        variant='outlined'
        size='small'
        fullWidth
        disabled={disabled}
        placeholder={placeholder}
        error={error}
        sx={{
          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
        }}
        onChange={handleChange}
      />
    </CellStack>
  );
});

ValidationTextCell.displayName = 'ValidationTextCell';

type ValidationSelectCellProps = {
  rowId: string;
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
  helperText?: string;
  helperError?: boolean;
  onChange: (rowId: string, value: string) => void;
};

const ValidationSelectCell = memo(({ rowId, value, options, placeholder, disabled = false, error = false, warning = false, helperText = '', helperError = false, onChange }: ValidationSelectCellProps) => {
  const handleChange = useCallback((e: any) => onChange(rowId, e.target.value), [onChange, rowId]);
  const borderColor = error
    ? palette.error.main
    : warning
      ? palette.warning.main
      : palette.secondary.main;
  return (
    <CellStack helperText={helperText} helperError={helperError}>
      <SelectComponent
        value={value}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        sx={{
          ...cellSelectSx,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${borderColor} !important`,
          },
        }}
        onChange={handleChange}
      />
    </CellStack>
  );
});

ValidationSelectCell.displayName = 'ValidationSelectCell';

type ValidationAutocompleteCellProps = {
  rowId: string;
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  helperError?: boolean;
  onChange: (rowId: string, value: string) => void;
};

const ValidationAutocompleteCell = memo(({ rowId, value, options, placeholder, disabled = false, error = false, helperText = '', helperError = false, onChange }: ValidationAutocompleteCellProps) => {
  const theme = useTheme();
  const selectBorderColor = darken(theme.palette.background.default, 0.2);
  const selectedOption = options.find((o) => o.value === value) ?? null;

  const handleChange = useCallback(
    (_: any, newValue: { label: string; value: string } | null) => {
      onChange(rowId, newValue?.value ?? '');
    },
    [onChange, rowId]
  );

  return (
    <CellStack helperText={helperText} helperError={helperError}>
      {/* <Tooltip
        title={selectedOption?.label || ''}
        arrow
        placement='right'
        disableHoverListener={!selectedOption?.label}
        slotProps={{ tooltip: { sx: validationTooltipSx } }}
      > */}
      <Autocomplete
        value={selectedOption}
        options={options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        onChange={handleChange}
        disabled={disabled}
        popupIcon={<DownIcon style={{ color: palette.info.dark, opacity: disabled ? '0.5' : '0.7' }} />}
        disableClearable={false}
        size='small'
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <TruncatedTypography sx={{ width: '100%', display: 'block', fontSize: '1rem', color: palette.info.dark }}>
                {option.label}
              </TruncatedTypography>
            </Box>
          </li>
        )}
        ListboxProps={{
          sx: {
            p: 0,
            maxHeight: '224px',
            overflow: 'auto',
            backgroundColor: palette.primary.contrastText,
            '& .MuiAutocomplete-option': {
              minHeight: '48px',
              fontSize: '1rem',
              fontWeight: 400,
              color: palette.info.dark,
              py: '6px',
              px: '16px',
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: 'rgba(92, 133, 255, 0.08) !important',
              color: `${palette.info.dark} !important`,
            },
            '& .MuiAutocomplete-option.Mui-focused': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: `${palette.info.dark} !important`,
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
              backgroundColor: 'rgba(92, 133, 255, 0.08) !important',
              color: `${palette.info.dark}`,
            },
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: '0px',
              borderRadius: '12px',
              border: `2px solid ${selectBorderColor}`,
              boxShadow: 'none',
              backgroundColor: palette.primary.contrastText,
              overflow: 'hidden',
            },
          },
        }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '1rem',
            color: `${palette.info.dark}`,
            backgroundColor: '#FFFFFF',
            padding: '8.5px 14px !important',
          },
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'nowrap',
            paddingRight: '60px !important',
          },
          '& .MuiInputBase-input': {
            fontSize: '16px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: `${palette.info.dark} !important`,
          },
          '& .MuiAutocomplete-input': {
            padding: '0 !important',
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '16px',
            color: '#c8c8ca',
            fontStyle: 'italic',
            opacity: 1,
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: error ? '#D81010 !important' : `${selectBorderColor} !important`,
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: error ? '#D81010 !important' : `${selectBorderColor} !important`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: error ? '#D81010 !important' : '#D9DFED !important',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={error}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? '#D81010 !important' : '#D9DFED !important',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '16px',
                color: '#c8c8ca',
                fontStyle: 'italic',
                opacity: 1,
              },
            }}
          />
        )}
      />
      {/* </Tooltip> */}
    </CellStack>
  );
});

ValidationAutocompleteCell.displayName = 'ValidationAutocompleteCell';

type ValidationSecretCellProps = {
  rowId: string;
  secretKey: string;
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
  helperText?: string;
  helperError?: boolean;
  onChange: (rowId: string, secretKey: string, value: string) => void;
};

const ValidationSecretCell = memo(
  ({ rowId, secretKey, value, options, placeholder, disabled = false, error = false, warning = false, helperText = '', helperError = false, onChange }: ValidationSecretCellProps) => {
    const handleChange = useCallback((e: any) => onChange(rowId, secretKey, e.target.value), [onChange, rowId, secretKey]);
    const borderColor = error
      ? palette.error.main
      : warning
        ? palette.warning.main
        : palette.secondary.main;
    return (
      <CellStack helperText={helperText} helperError={helperError}>
        <SelectComponent
          value={value}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          sx={{
            ...cellSelectSx,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
          }}
          onChange={handleChange}
        />
      </CellStack>
    );
  }
);

ValidationSecretCell.displayName = 'ValidationSecretCell';

type ValidationDeleteCellProps = {
  rowId: string;
  onDelete: (rowId: string) => void;
};

const ValidationDeleteCell = memo(({ rowId, onDelete }: ValidationDeleteCellProps) => {
  const handleDelete = useCallback(() => onDelete(rowId), [onDelete, rowId]);
  return <DeleteIconButton onClick={handleDelete} isFrom={true} />;
});

ValidationDeleteCell.displayName = 'ValidationDeleteCell';

const FlowImportValidationModal = ({
  open,
  isLoading = false,
  selectedGatewayName,
  parserWarnings,
  validationErrors,
  validationRows,
  componentOptions,
  componentSecretFieldsMap,
  componentRequiredSecretsMap,
  secretsData,
  canSave,
  onClose,
  onSkipScan,
  onScanModified,
  onSave,
}: FlowImportValidationModalProps) => {
  const [rows, setRows] = useState<ValidationRow[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(7);
  const stableTableHeight = `${(pageSize + 1) * TABLE_ROW_HEIGHT}px`;
  const [sortState, setSortState] = useState<SortState>({ field: '', sort: '' });
  const [loadCycleStarted, setLoadCycleStarted] = useState(false);
  const themeData: any = useTheme();

  useEffect(() => {
    if (open) {
      setRows(validationRows);
      setSearch('');
      setPage(1);
      setSortState({ field: '', sort: '' });
      setLoadCycleStarted(false);
    }
  }, [open, validationRows, parserWarnings, validationErrors]);

  useEffect(() => {
    if (!open) return;
    if (isLoading) {
      setLoadCycleStarted(true);
    }
  }, [open, isLoading]);

  const uniqueAssetRows = useMemo(() => {
    const seen = new Set<number>();
    return rows.filter((row) => {
      if (seen.has(row.asset_index)) return false;
      seen.add(row.asset_index);
      return true;
    });
  }, [rows]);

  const buildDuplicateKeySet = useCallback((rowList: ValidationRow[], selector: (row: ValidationRow) => string) => {
    const counts = new Map<string, number>();
    rowList.forEach((row) => {
      const key = selector(row).trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([key]) => key));
  }, []);

  const duplicateAssetNames = useMemo(() => buildDuplicateKeySet(uniqueAssetRows, (row) => String(row.asset_name || '')), [buildDuplicateKeySet, uniqueAssetRows]);
  const duplicateAssetIps = useMemo(() => buildDuplicateKeySet(uniqueAssetRows, (row) => String(row.asset_ip || '')), [buildDuplicateKeySet, uniqueAssetRows]);

  const duplicateComponentKeysWithinAsset = useMemo(() => {
    const counts = new Map<string, number>();

    rows.forEach((row) => {
      const componentKey = String(row.component_name || '').trim().toLowerCase();
      if (!componentKey) return;
      const key = `${String(row.asset_index)}::${componentKey}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([key]) => key));
  }, [rows]);

  const normalizeComponentToken = useCallback(
    (value: string) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ''),
    []
  );


  const componentOptionExactMap = useMemo(() => {
    const map = new Map<string, string>();
    componentOptions.forEach((option) => {
      const normalized = String(option.value || '').trim().toLowerCase();
      if (normalized) map.set(normalized, String(option.value || '').trim());
    });
    return map;
  }, [componentOptions]);

  const componentOptionTokenMap = useMemo(() => {
    const map = new Map<string, string>();
    componentOptions.forEach((option) => {
      const optionValue = String(option.value || '').trim();
      const valueToken = normalizeComponentToken(optionValue);
      const labelToken = normalizeComponentToken(String(option.label || ''));
      if (valueToken && !map.has(valueToken)) map.set(valueToken, optionValue);
      if (labelToken && !map.has(labelToken)) map.set(labelToken, optionValue);
    });
    return map;
  }, [componentOptions, normalizeComponentToken]);

  const resolveComponentName = useCallback(
    (rawName: string) => {
      const directKey = String(rawName || '').trim().toLowerCase();
      if (!directKey) return '';
      const direct = componentOptionExactMap.get(directKey);
      if (direct) return direct;
      const tokenMatch = componentOptionTokenMap.get(normalizeComponentToken(rawName));
      return tokenMatch || '';
    },
    [componentOptionExactMap, componentOptionTokenMap, normalizeComponentToken]
  );

  const componentOptionKeySet = useMemo(() => {
    return new Set(componentOptions.map((option) => String(option.value || '').trim().toLowerCase()).filter(Boolean));
  }, [componentOptions]);

  useEffect(() => {
    if (!open) return;
    setRows((prev) =>
      prev.map((row) => {
        const rawComponentName = String(row.component_name || '').trim();
        if (!rawComponentName) return row;
        const resolved = resolveComponentName(rawComponentName);
        if (!resolved || resolved === rawComponentName) return row;
        return { ...row, component_name: resolved };
      })
    );
  }, [open, resolveComponentName]);

  const globalSecretOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (secretsData || [])
            .map((secret: any) => String(secret?.secret_name || '').trim())
            .filter(Boolean)
        )
      ),
    [secretsData]
  );

  const activeSecretFields = useMemo(() => {
    const byKey = new Map<string, SecretFieldDef>();

    uniqueAssetRows.forEach((row) => {
      const componentKey = String(row.component_name || '').trim().toLowerCase();
      if (!componentKey) return;
      const fields = componentSecretFieldsMap[componentKey] || [];
      fields.forEach((field) => {
        if (!byKey.has(field.key)) {
          byKey.set(field.key, field);
        }
      });
    });

    // Keep imported secret fields visible even when component is invalid/mismatched.
    rows.forEach((row) => {
      const rowSecrets = row.secrets || {};
      Object.keys(rowSecrets).forEach((key) => {
        if (!/^secret_/i.test(key)) return;
        if (!byKey.has(key)) {
          byKey.set(key, {
            key,
            label: key
              .replace(/^secret_/i, '')
              .split('_')
              .filter(Boolean)
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' '),
            isDropdown: true,
            options: globalSecretOptions,
          });
        }
      });
    });

    return Array.from(byKey.values());
  }, [uniqueAssetRows, rows, componentSecretFieldsMap, globalSecretOptions]);

  const getIssueText = useCallback((row: ValidationRow) => String(row.issues || '').trim(), []);
  const getIssueTextLower = useCallback((row: ValidationRow) => getIssueText(row).toLowerCase(), [getIssueText]);

  const hasDuplicateWithExistingAsset = useCallback((row: ValidationRow) => {
    const issues = getIssueTextLower(row);
    return (
      issues.includes('duplicate with existing asset under selected gateway') ||
      issues.includes('asset already exists in gateway') ||
      issues.includes('asset already exists in the selected gateway')
    );
  }, [getIssueTextLower]);

  const hasDuplicateWithinImport = useCallback((row: ValidationRow) => {
    const issues = getIssueTextLower(row);
    return issues.includes('duplicate with another imported asset') || issues.includes('duplicate asset in import');
  }, [getIssueTextLower]);

  const hasInlineDuplicateComponentWithinAsset = useCallback(
    (row: ValidationRow) => {
      const componentKey = String(row.component_name || '').trim().toLowerCase();
      if (!componentKey) return false;
      const key = `${String(row.asset_index)}::${componentKey}`;
      return duplicateComponentKeysWithinAsset.has(key);
    },
    [duplicateComponentKeysWithinAsset]
  );

  const getInlineError = useCallback((row: ValidationRow, field: string) => {
    const assetNameKey = String(row.asset_name || '').trim().toLowerCase();

    if (field === 'asset_name') {
      if (!String(row.asset_name || '').trim()) return true;
      if (duplicateAssetNames.has(assetNameKey)) return true;
    }
    if (field === 'asset_ip') {
      if (!String(row.asset_ip || '').trim()) return true;
    }
    if (field === 'asset_status') {
      return !String(row.asset_status || '').trim();
    }
    // Component name is no longer editable in the table; errors are shown in warnings section
    if (field === 'component_name') {
      return false;
    }
    if (field === 'component_status') {
      return !String(row.component_status || '').trim();
    }

    return false;
  }, [duplicateAssetNames]);

  const getInlineFieldHint = useCallback((row: ValidationRow, field: 'asset_name' | 'asset_ip') => {
    const value = String(field === 'asset_name' ? row.asset_name : row.asset_ip).trim();
    const key = value.toLowerCase();
    if (!value) return `${field === 'asset_name' ? 'Asset name' : 'Asset IP'} is required.`;
    if (field === 'asset_name' && duplicateAssetNames.has(key))
      return `Duplicate asset name: "${value}".`;
    return '';
  }, [duplicateAssetNames]);

  const getAssetNameHelperText = useCallback((row: ValidationRow) => {
    const messages: string[] = [];
    const base = getInlineFieldHint(row, 'asset_name');
    if (base) messages.push(base);
    if (hasDuplicateWithExistingAsset(row)) {
      messages.push('Asset already exists in the selected gateway.');
    }
    if (hasDuplicateWithinImport(row)) {
      messages.push('Duplicate asset in import.');
    }
    return messages.join(' ');
  }, [getInlineFieldHint, hasDuplicateWithExistingAsset, hasDuplicateWithinImport]);

  const isAssetNameDuplicateInGateway = useCallback((row: ValidationRow) => hasDuplicateWithExistingAsset(row), [hasDuplicateWithExistingAsset]);

  const isAssetNameInlineError = useCallback((row: ValidationRow) => {
    const assetName = String(row.asset_name || '').trim();
    if (!assetName) return true;
    return duplicateAssetNames.has(assetName.toLowerCase());
  }, [duplicateAssetNames]);

  const getAssetIpHelperText = useCallback((row: ValidationRow) => {
    const messages: string[] = [];
    const base = getInlineFieldHint(row, 'asset_ip');
    if (base) messages.push(base);
    const ip = String(row.asset_ip || '').trim();
    if (ip && duplicateAssetIps.has(ip.toLowerCase())) {
      messages.push(`Duplicate IP: "${ip}" (allowed).`);
    }
    return messages.join(' ');
  }, [duplicateAssetIps, getInlineFieldHint]);

  const secretIdToNameMap = useMemo(() => {
    const map = new Map<number, string>();
    (secretsData || []).forEach((secret: any) => {
      const id = Number(secret?.secret_id);
      const name = String(secret?.secret_name || '').trim();
      if (Number.isFinite(id) && name) map.set(id, name);
    });
    return map;
  }, [secretsData]);

  const secretNameToTokenMap = useMemo(() => {
    const map = new Map<string, string>();
    (secretsData || []).forEach((secret: any) => {
      const id = Number(secret?.secret_id);
      const name = String(secret?.secret_name || '').trim();
      if (Number.isFinite(id) && name) map.set(name.toLowerCase(), `secret:${id}`);
    });
    return map;
  }, [secretsData]);

  const resolveSecretDisplayValue = useCallback(
    (rawValue: string) => {
      const value = String(rawValue || '').trim();
      const match = value.match(/^secret:(\d+)$/i);
      if (!match) return value;
      const id = Number(match[1]);
      return secretIdToNameMap.get(id) || value;
    },
    [secretIdToNameMap]
  );

  const getSecretFieldContext = useCallback((row: ValidationRow, secretField: SecretFieldDef) => {
    const resolvedComponent = resolveComponentName(String(row.component_name || '').trim()) || String(row.component_name || '').trim();
    const componentKey = String(resolvedComponent || '').trim().toLowerCase();
    const isKnownComponent = componentOptionKeySet.has(componentKey);
    const componentFields = componentSecretFieldsMap[componentKey] || [];
    const fieldConfig = componentFields.find((field) => field.key === secretField.key) || secretField;
    const rawValue = String(row.secrets?.[secretField.key] || '').trim();
    const selectedDisplayValue = resolveSecretDisplayValue(rawValue);

    const options = Array.from(
      new Set(
        [...(fieldConfig.options || []), ...globalSecretOptions]
          .map((option) => String(option).trim())
          .filter(Boolean)
      )
    );
    const isFieldApplicable =
      (isKnownComponent && componentFields.some((field) => field.key === secretField.key)) ||
      Boolean(rawValue);
    const isDropdown = Boolean(fieldConfig.isDropdown);
    const hasOptions = options.length > 0;
    const optionKeySet = new Set(options.map((option) => option.toLowerCase()));
    const hasInvalidDropdownValue =
      isFieldApplicable &&
      hasOptions &&
      selectedDisplayValue.length > 0 &&
      !optionKeySet.has(selectedDisplayValue.toLowerCase());

    return {
      isFieldApplicable,
      rawValue,
      selectedDisplayValue,
      options,
      isDropdown,
      hasOptions,
      hasInvalidDropdownValue,
    };
  }, [componentOptionKeySet, componentSecretFieldsMap, globalSecretOptions, resolveComponentName, resolveSecretDisplayValue]);

  const getSecretFieldHint = useCallback((row: ValidationRow, secretField: SecretFieldDef) => {
    const context = getSecretFieldContext(row, secretField);
    if (!context.isFieldApplicable) return '';
    if (!context.hasOptions) {
      if (context.rawValue) return `Secret "${context.rawValue}" not configured.`;
      return 'No secrets configured.';
    }
    if (context.hasInvalidDropdownValue) return `Invalid secret: "${context.rawValue}".`;
    return '';
  }, [getSecretFieldContext]);

  const getRowRequiredSecretCoverage = useCallback((row: ValidationRow) => {
    const resolvedComponent = resolveComponentName(String(row.component_name || '').trim()) || String(row.component_name || '').trim();
    const componentKey = String(resolvedComponent || '').trim().toLowerCase();
    if (!componentOptionKeySet.has(componentKey)) {
      return { applies: false, meets: true, missing: [] as string[] };
    }

    const requiredSecretKeys = (componentRequiredSecretsMap[componentKey] || []).map((key) => String(key).trim()).filter(Boolean);
    if (!requiredSecretKeys.length) {
      return { applies: false, meets: true, missing: [] as string[] };
    }

    const secretFields = componentSecretFieldsMap[componentKey] || [];
    const selectedSecrets = secretFields
      .map((field) => String(row?.secrets?.[field.key] ?? '').trim())
      .filter(Boolean);

    const collectedKeys = new Set<string>();
    selectedSecrets.forEach((selectedSecret) => {
      const lowerSelected = selectedSecret.toLowerCase();
      let secret = (secretsData || []).find(
        (s: any) => String(s?.secret_name || '').trim().toLowerCase() === lowerSelected
      );

      if (!secret && lowerSelected.startsWith('secret:')) {
        const parsedId = Number(lowerSelected.split(':')[1]);
        if (!Number.isNaN(parsedId)) {
          secret = (secretsData || []).find((s: any) => Number(s?.secret_id) === parsedId);
        }
      }

      const keyList = Array.isArray(secret?.secret_keys_list) ? secret.secret_keys_list : [];
      keyList.forEach((key: string) => collectedKeys.add(String(key).trim()));
    });

    const missing = requiredSecretKeys.filter((requiredKey) => !collectedKeys.has(requiredKey));
    return { applies: true, meets: missing.length === 0, missing };
  }, [componentOptionKeySet, componentRequiredSecretsMap, componentSecretFieldsMap, secretsData, resolveComponentName]);

  const getComponentIssueWarning = useCallback((row: ValidationRow): string | null => {
    const componentName = String(row.component_name || '').trim();
    const resolvedComponentName = resolveComponentName(componentName);
    const componentKey = (resolvedComponentName || componentName).toLowerCase();

    if (!componentName) {
      return 'Component is required.';
    }
    if (!componentOptionKeySet.has(componentKey)) {
      return `Invalid component: "${componentName}".`;
    }
    if (hasInlineDuplicateComponentWithinAsset(row)) {
      return `Duplicate component: "${componentName}".`;
    }
    return null;
  }, [componentOptionKeySet, hasInlineDuplicateComponentWithinAsset, resolveComponentName]);

  const hasInlineValidationErrors = useMemo(() => {
    return rows.some((row) => {
      // const hasSecretFieldErrors = activeSecretFields.some((secretField) => {
      //   return getSecretFieldContext(row, secretField).hasInvalidDropdownValue;
      // });

      return (
        getInlineError(row, 'asset_name') ||
        getInlineError(row, 'asset_ip') ||
        getInlineError(row, 'asset_status') ||
        getInlineError(row, 'component_name') ||
        getInlineError(row, 'component_status')
        // || hasSecretFieldErrors
      );
    });
  }, [rows, getInlineError]);

  const hasOutOfScopeComponents = useMemo(() => {
    return rows.some((row) => {
      const rawName = String(row.component_name || '').trim();
      if (!rawName) return true;
      const resolved = resolveComponentName(rawName);
      const normalized = String(resolved || rawName).trim().toLowerCase();
      return !componentOptionKeySet.has(normalized);
    });
  }, [rows, resolveComponentName, componentOptionKeySet]);

  const isSaveDisabled =
    isLoading ||
    !canSave ||
    hasInlineValidationErrors ||
    hasOutOfScopeComponents;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const secretValues = activeSecretFields
        .map((field) => String(row.secrets?.[field.key] || ''))
        .join(' ');

      return [
        row.asset_name,
        row.asset_ip,
        row.component_name,
        row.component_status,
        row.asset_status,
        secretValues,
        row.issues,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, activeSecretFields]);

  const sortedRows = useMemo(() => {
    if (!sortState.field || !sortState.sort) return filteredRows;

    const sortableFields = new Set(['asset_name', 'asset_ip', 'asset_status', 'component_name', 'component_status']);
    if (!sortableFields.has(sortState.field)) return filteredRows;

    const direction = sortState.sort === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const aValue = String(a[sortState.field as keyof ValidationRow] ?? '').trim();
      const bValue = String(b[sortState.field as keyof ValidationRow] ?? '').trim();
      return direction * aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [filteredRows, sortState]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    setPage((prevPage) => {
      if (!Number.isFinite(prevPage) || prevPage < 1) return 1;
      if (prevPage > pageCount) return pageCount;
      return prevPage;
    });
  }, [pageCount]);

  const updateRow = useCallback((id: string, patch: Partial<ValidationRow>) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const hasAnyChange = Object.keys(patch).some((key) => {
          const typedKey = key as keyof ValidationRow;
          return row[typedKey] !== patch[typedKey];
        });
        if (!hasAnyChange) return row;
        const nextRow = { ...row, ...patch };
        return nextRow;
      })
    );
  }, []);

  const updateAssetStatusForAsset = useCallback((assetIndex: number, nextStatus: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.asset_index !== assetIndex) return row;
        if (row.asset_status === nextStatus) return row;
        return { ...row, asset_status: nextStatus };
      })
    );
  }, []);

  const updateAssetFieldForAsset = useCallback(
    (assetIndex: number, patch: Partial<Pick<ValidationRow, 'asset_name' | 'asset_ip'>>) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.asset_index !== assetIndex) return row;
          const nextRow = { ...row, ...patch, issues: '' };
          const hasChanged =
            nextRow.asset_name !== row.asset_name ||
            nextRow.asset_ip !== row.asset_ip ||
            nextRow.issues !== row.issues;
          return hasChanged ? nextRow : row;
        })
      );
    },
    []
  );

  const updateRowSecret = useCallback((id: string, secretKey: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (String(row.secrets?.[secretKey] || '') === value) return row;
        const nextSecrets = { ...(row.secrets || {}) };
        nextSecrets[secretKey] = value;
        return { ...row, secrets: nextSecrets };
      })
    );
  }, []);

  const updateRowSecretFromDisplay = useCallback(
    (id: string, secretKey: string, value: string) => {
      const display = String(value || '').trim();
      const canonical = display ? (secretNameToTokenMap.get(display.toLowerCase()) || display) : '';
      updateRowSecret(id, secretKey, canonical);
    },
    [secretNameToTokenMap, updateRowSecret]
  );

  const handleSecretTextChange = useCallback(
    (secretKey: string) => (rowId: string, value: string) => updateRowSecret(rowId, secretKey, value),
    [updateRowSecret]
  );

  const deleteRow = useCallback((id: string) => {
    setRows((prev) => {
      const nextRows = prev.filter((row) => row.id !== id);
      const nextPageCount = Math.max(1, Math.ceil(nextRows.length / pageSize));

      setPage((prevPage) => {
        if (!Number.isFinite(prevPage) || prevPage < 1) return 1;
        if (prevPage > nextPageCount) return nextPageCount;
        return prevPage;
      });

      return nextRows;
    });
  }, [pageSize]);

  const handleSearchChange = useCallback((value: string) => setSearch(value), []);
  const handlePagination = useCallback((_rowsPerPage: number, newPage: number) => {
    setPage(Number.isFinite(newPage) && newPage > 0 ? newPage : 1);
  }, []);
  const handleSorting = useCallback((sortModel: SortState) => {
    setSortState({ field: sortModel.field || '', sort: sortModel.sort || '' });
    setPage(1);
  }, []);
  const handleSave = useCallback(() => onSave(rows), [onSave, rows]);
  const handleSkipScan = useCallback(() => onSkipScan(rows), [onSkipScan, rows]);
  const handleScanModified = useCallback(() => onScanModified(rows), [onScanModified, rows]);

  const secretColumns = useMemo(
    () =>
      activeSecretFields.map((secretField) => ({
        field: `secret_${secretField.key}`,
        headerName: noWrapHeader(`${secretField.label.toUpperCase()}`),
        headerAlign: 'left',
        minWidth: 200,
        maxWidth: 300,
        renderCell: ({ row }: any) => {
          const context = getSecretFieldContext(row, secretField);
          const requiredCoverage = getRowRequiredSecretCoverage(row);
          const hasRequiredCoverageWarning = requiredCoverage.applies && !requiredCoverage.meets && context.isFieldApplicable;
          const hasValidSelectedSecret =
            context.isDropdown &&
            context.isFieldApplicable &&
            context.hasOptions &&
            Boolean(context.selectedDisplayValue) &&
            !context.hasInvalidDropdownValue;
          const showRequiredCoverageWarning = hasRequiredCoverageWarning && !hasValidSelectedSecret;
          const baseHint = getSecretFieldHint(row, secretField);
          const secretHint = context.hasInvalidDropdownValue
            ? baseHint
            : showRequiredCoverageWarning
              ? `Missing keys: ${requiredCoverage.missing.join(', ')}.`
              : baseHint;
          // When no API secrets exist but import has a value, include it so the dropdown shows the value (read-only)
          const baseOptions = !context.hasOptions && context.rawValue
            ? [context.rawValue]
            : context.options;
          const secretOptions = baseOptions.map((option) => ({ label: option, value: option }));
          const showSecretWarning = context.hasInvalidDropdownValue || showRequiredCoverageWarning;

          return (
            // <Tooltip
            //   title={secretHint}
            //   arrow
            //   placement='right'
            //   slotProps={{ tooltip: { sx: validationTooltipSx } }}
            // >
            <Box sx={{ width: '100%' }}>
              {context.isDropdown ? (
                <ValidationSecretCell
                  rowId={row.id}
                  secretKey={secretField.key}
                  // value={context.hasInvalidDropdownValue ? '' : context.rawValue}
                  value={context.selectedDisplayValue}
                  options={secretOptions}
                  placeholder={!context.isFieldApplicable ? 'N/A' : (!context.hasOptions && !context.rawValue) ? 'No secrets available' : 'Select Secret'}
                  disabled={!context.isFieldApplicable || !context.hasOptions}
                  error={false}
                  warning={showSecretWarning}
                  helperText={secretHint || ' '}
                  helperError={false}
                  onChange={updateRowSecretFromDisplay}
                />
              ) : (
                <ValidationTextCell
                  rowId={row.id}
                  value={context.rawValue}
                  disabled={!context.isFieldApplicable}
                  placeholder={context.isFieldApplicable ? '' : 'N/A'}
                  warning={showSecretWarning}
                  helperText={secretHint || ' '}
                  helperError={false}
                  onChange={handleSecretTextChange(secretField.key)}
                />
              )}
            </Box>
            // </Tooltip>
          );
        },
      })),
    [activeSecretFields, getSecretFieldContext, getSecretFieldHint, getRowRequiredSecretCoverage, handleSecretTextChange, updateRowSecretFromDisplay]
  );

  const columns = useMemo(
    () => [
      {
        field: 'asset_name',
        headerName: noWrapHeader('ASSET NAME'),
        sortable: true,
        headerAlign: 'left',
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const helperText = getAssetNameHelperText(row);
          const hasError = isAssetNameInlineError(row);
          const hasWarning = !hasError && Boolean(helperText);
          return (
            <Box sx={{ width: '100%' }}>
              <ValidationTextCell
                rowId={row.id}
                value={row.asset_name ?? ''}
                error={hasError}
                warning={hasWarning}
                helperText={helperText || ' '}
                helperError={hasError}
                onChange={(_id, value) => updateAssetFieldForAsset(row.asset_index, { asset_name: value })}
              />
            </Box>
          );
        },
      },
      {
        field: 'asset_ip',
        headerName: noWrapHeader('ASSET IP'),
        sortable: true,
        headerAlign: 'left',
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const helperText = getAssetIpHelperText(row);
          const hasError = getInlineError(row, 'asset_ip');
          const hasWarning = !hasError && Boolean(helperText);
          return (
            <Box sx={{ width: '100%' }}>
              <ValidationTextCell
                rowId={row.id}
                value={row.asset_ip ?? ''}
                error={hasError}
                warning={hasWarning}
                helperText={helperText}
                helperError={hasError}
                onChange={(_id, value) => updateAssetFieldForAsset(row.asset_index, { asset_ip: value })}
              />
            </Box>
          );
        },
      },
      {
        field: 'asset_status',
        headerName: noWrapHeader('ASSET STATUS'),
        sortable: true,
        headerAlign: 'left',
        width: 70,
        maxWidth: 150,
        renderCell: ({ row }: any) => {
          const hasError = getInlineError(row, 'asset_status');
          const helperText = hasError ? 'Asset status is required.' : ' ';

          return (
            <ValidationSelectCell
              rowId={row.id}
              value={row.asset_status ?? ''}
              options={STATUS_OPTIONS}
              placeholder='Select Status'
              error={hasError}
              warning={false}
              helperText={helperText}
              helperError={hasError}
              onChange={(_id, value) => updateAssetStatusForAsset(row.asset_index, value)}
            />
          );
        },
      },
      {
        field: 'component_name',
        headerName: noWrapHeader('COMPONENT NAME'),
        sortable: false,
        headerAlign: 'left',
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const currentComponentName = String(row.component_name || '').trim();
          const resolvedComponentName = resolveComponentName(currentComponentName);
          const normalizedComponentKey = String(resolvedComponentName || currentComponentName).toLowerCase();

          const isMissingComponent = !currentComponentName;
          const isInvalidComponent = !!currentComponentName && !componentOptionKeySet.has(normalizedComponentKey);
          const isDuplicateComponent = hasInlineDuplicateComponentWithinAsset(row);

          const hasError = isMissingComponent || isInvalidComponent;
          const hasWarning = !hasError && isDuplicateComponent;

          const helperText = isMissingComponent
            ? 'Component is required.'
            : isInvalidComponent
              ? `Invalid component: "${currentComponentName}".`
              : isDuplicateComponent
                ? `Duplicate component: "${currentComponentName}".`
                : ' ';

          return (
            <Box sx={{ width: '100%' }}>
              <ValidationTextCell
                rowId={row.id}
                value={resolvedComponentName || currentComponentName}
                disabled={true}
                error={hasError}
                warning={hasWarning}
                helperText={helperText}
                helperError={hasError}
                onChange={() => { }}
              />
            </Box>
          );
        },
      },
      {
        field: 'component_status',
        headerName: noWrapHeader('COMPONENT STATUS'),
        sortable: true,
        headerAlign: 'left',
        width: 70,
        maxWidth: 150,
        renderCell: ({ row }: any) => {
          const hasError = getInlineError(row, 'component_status');
          const helperText = hasError ? 'Component status is required.' : ' ';

          return (
            <ValidationSelectCell
              rowId={row.id}
              value={row.component_status ?? ''}
              options={STATUS_OPTIONS}
              placeholder='Select Status'
              error={hasError}
              warning={false}
              helperText={helperText}
              helperError={hasError}
              onChange={(id, value) => updateRow(id, { component_status: value })}
            />
          );
        },
      },
      ...secretColumns,
      {
        field: 'delete',
        headerName: '',
        width: 40,
        sortable: false,
        renderCell: ({ row }: any) => <ValidationDeleteCell rowId={row.id} onDelete={deleteRow} />,
      },
    ],
    [
      componentOptionKeySet,
      componentOptions,
      deleteRow,
      getInlineError,
      getInlineFieldHint,
      secretColumns,
      updateAssetFieldForAsset,
      updateAssetStatusForAsset,
      updateRow,
      resolveComponentName,
    ]
  );

  const tableRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [pageSize, safePage, sortedRows]);

  const showInitialLoadingState = isLoading || (!loadCycleStarted && rows.length === 0);
  const noRowsLabel = showInitialLoadingState ? '' : 'No entries found';

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          ...Styles.dialog.muiPaper,
          maxWidth: '100%',
          width: '100vw',
          minHeight: '98vh',
          maxHeight: '98vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: themeData.palette.background.default,
          margin: '12px',
          // borderRadius: 0,
        },
      }}
    >
      <DialogTitle id='flow-import-validation-title' sx={{ ...Styles.dialogTitle, color: themeData.palette.text.primary }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2, flexWrap: 'wrap', padding: '24px', borderBottom: '1px solid #D9DFED80' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='subtitle1' sx={{ ...Styles.dialogTitle, padding: 0, color: themeData.palette.text.primary }}>
              List of Assets
            </Typography>
            <Typography variant='caption' sx={{ color: themeData.palette.text.secondary, mt: 0, display: 'block' }}>
              Review asset and component information.
            </Typography>
          </Box>
          <SearchField
            searchPlaceholder='Search by Asset Name / IP'
            defaultValue={search}
            onSearch={handleSearchChange}
            width='300px'
          />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          '&.MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            padding: '0px !important',
            flex: 1,
            overflow: 'hidden',
            minHeight: 0,
          },
        }}
      >
        {/* Table — flex: 1 fills remaining dialog height; overflow: auto makes table+pagination scroll within this box */}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            overflow: 'auto',
            px: 0,
          }}
        >
          {showInitialLoadingState && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              <CircularProgress size={25} />
            </Box>
          )}
          <CustomDataTable
            rows={tableRows}
            columns={columns}
            tableHeight='calc(100vh - 285px)'
            rowHeight={TABLE_ROW_HEIGHT}
            rowsPerPage={pageSize}
            isServerPagination
            totalRowCount={sortedRows.length}
            page={safePage}
            onPagination={handlePagination}
            onSorting={handleSorting}
            getRowId={(row: ValidationRow) => row.id}
            loading={showInitialLoadingState}
            noRowsLabel={noRowsLabel}
            isFromBulkAssetsPopup={true}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            pt: 2,
            pb: 3,
            px: 0,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            flexShrink: 0,
            mt: 'auto',
            borderTop: '1px solid #D9DFED80'
          }}
        >
          <Button variant='outlined' onClick={onClose} size='small' sx={{ minWidth: 119, whiteSpace: 'nowrap' }}>
            Cancel
          </Button>
          <Button
            variant='outlined'
            onClick={handleSkipScan}
            disabled={isLoading || !canSave}
            size='small'
            sx={{ minWidth: 119, whiteSpace: 'nowrap' }}
          >
            Skip Scan
          </Button>
          <Button
            variant='outlined'
            onClick={handleScanModified}
            disabled={isSaveDisabled}
            size='small'
            sx={{ minWidth: 166, whiteSpace: 'nowrap' }}
          >
            Scan Modified
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isSaveDisabled}
            size='small'
            sx={{ minWidth: 119, whiteSpace: 'nowrap' }}
          >
            Scan All
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(FlowImportValidationModal);